import { createFeatureSelector, createSelector } from '@ngrx/store';

import { Chain } from '../../models/chain.interface';
import { Transformation } from '../../models/transformation';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { TRANSFORMATIONS_REDUCER_KEY } from '../reducers';
import { getSelectedAppRestApiName$ } from './apps.selector';
import { getAssociatedOrganizationsMap$, getCurrentOrganization$ } from './locations.selector';
import { getCurrentOrganizationId$ } from './session.selector';

import type { TransformationsState } from '../states';
const currentState = createFeatureSelector<TransformationsState>(TRANSFORMATIONS_REDUCER_KEY);

export const getAllTransformationsServicesMap$ = createSelector(
    currentState,
    getCurrentOrganizationId$,
    (state, organizationId) => state.organizationsMap?.[organizationId]?.services
);

export const getServiceTransformations$ = (restApiName: string) =>
    createSelector(getAllTransformationsServicesMap$, map => map?.[restApiName]);

export const getActiveInboundTransformations$ = (restApiName: string) =>
    createSelector(getAllTransformationsServicesMap$, services => services?.[restApiName]?.transformations?.activeTransformations?.inbound);

export const getActiveOutboundTransformations$ = (restApiName: string) =>
    createSelector(
        getAllTransformationsServicesMap$,
        services => services?.[restApiName]?.transformations?.activeTransformations?.outbound
    );

export const getInboundDetailedTransformations$ = (restApiName: string) =>
    createSelector(
        getServiceTransformations$(restApiName),
        getActiveInboundTransformations$(restApiName),
        getCurrentOrganization$,
        (map, activeInboundTrs = [], currentOrganization) => {
            const providerOrgId = currentOrganization?.providerOrganizationId;
            const organizationsMap: {
                orgName: string;
                repositoryId: string;
                transformations: Transformation[];
            }[] = [];
            Object.keys(map?.transformations?.inboundDetailed || {}).forEach(key => {
                if (!organizationsMap.find(orgMap => orgMap.repositoryId === key)) {
                    const rawOrgName = map?.relativeOrganizations?.[key] || key;
                    organizationsMap.push({
                        orgName: providerOrgId && key === providerOrgId ? 'Default' : rawOrgName,
                        repositoryId: key,
                        transformations: [],
                    });
                }
                const orgTransformations = map?.transformations?.inboundDetailed?.[key];
                const organizationMap = organizationsMap.find(orgMap => orgMap.repositoryId === key);
                Object.keys(orgTransformations || {}).forEach(transformationKey => {
                    organizationMap.transformations.push({
                        ...orgTransformations?.[transformationKey],
                        active: activeInboundTrs?.includes(transformationKey),
                    });
                });
                organizationMap?.transformations.sort((a, b) => {
                    if (activeInboundTrs?.includes(a.chainName) && activeInboundTrs?.includes(b.chainName)) {
                        return activeInboundTrs?.indexOf(a.chainName) - activeInboundTrs?.indexOf(b.chainName);
                    } else if (activeInboundTrs?.includes(a.chainName)) {
                        return -1;
                    } else if (activeInboundTrs?.includes(b.chainName)) {
                        return 1;
                    }
                    return 0;
                });
            });
            return organizationsMap;
        }
    );

export const getFileUploadTransformations$ = (restApiName: string) =>
    createSelector(getInboundDetailedTransformations$(restApiName), organizationsMap => {
        const organizationsList: {
            orgName: string;
            transformations: Transformation[];
            repositoryId: string;
        }[] = [];
        organizationsMap.forEach(orgMap => {
            const transformations = orgMap.transformations.filter(transformation => transformation.isFileUpload);
            transformations?.length > 0 &&
                organizationsList.push({
                    ...orgMap,
                    transformations,
                });
        });
        return organizationsList;
    });

export const getAssetToAssetTransformations$ = (restApiName: string) =>
    createSelector(getInboundDetailedTransformations$(restApiName), organizationsMap => {
        const organizationsList: {
            orgName: string;
            transformations: Transformation[];
            repositoryId: string;
        }[] = [];
        organizationsMap.forEach(orgMap => {
            const transformations = orgMap.transformations.filter(transformation => transformation.isAssetToAsset);
            transformations?.length > 0 &&
                organizationsList.push({
                    ...orgMap,
                    transformations,
                });
        });
        return organizationsList;
    });

export const getSearchAssetTransformations$ = (restApiName: string) =>
    createSelector(getInboundDetailedTransformations$(restApiName), organizationsMap => {
        const organizationsList: {
            orgName: string;
            transformations: Transformation[];
            repositoryId: string;
        }[] = [];
        organizationsMap.forEach(orgMap => {
            const transformations = orgMap.transformations.filter(transformation => transformation.isSearchAsset);
            transformations?.length > 0 &&
                organizationsList.push({
                    ...orgMap,
                    transformations,
                });
        });
        return organizationsList;
    });

export const getSearchOrAssetToAssetTransformations$ = restApiName =>
    createSelector(
        getInboundDetailedTransformations$(restApiName),
        getActiveInboundTransformations$(restApiName),
        getCurrentOrganization$,
        (organizationsMap, activeTransformations, currentOrganization) => {
            const providerOrgId = currentOrganization?.providerOrganizationId;
            const organizationsList: {
                orgName: string;
                transformations: Transformation[];
                repositoryId: string;
            }[] = [];
            organizationsMap.forEach(orgMap => {
                const isActiveInboundTrsExists = !!orgMap.transformations.find(
                    trans => activeTransformations?.length === 0 || trans.active
                );
                const transformations = orgMap.transformations.filter(
                    transformation =>
                        (isActiveInboundTrsExists &&
                            (activeTransformations?.length === 0 || transformation.active) &&
                            (transformation.isAssetToAsset || transformation.isSearchAsset)) ||
                        (!isActiveInboundTrsExists && (transformation.isAssetToAsset || transformation.isSearchAsset))
                );
                transformations?.length > 0 &&
                    organizationsList.push({
                        ...orgMap,
                        transformations,
                    });
            });
            return organizationsList.sort((a, b) => {
                if (providerOrgId && a.repositoryId === providerOrgId) {
                    return 1;
                }
                if (providerOrgId && b.repositoryId === providerOrgId) {
                    return -1;
                }
                return 0;
            });
        }
    );

export const getChainNamesByRestApiName$ = (restApiName: string) =>
    createSelector(getAllTransformationsServicesMap$, services => services?.[restApiName]?.chainDisplayNames);

export const getOutBoundTransformations$ = (serviceId: string) =>
    createSelector(
        getAllTransformationsServicesMap$,
        getActiveOutboundTransformations$(serviceId),
        getAssociatedOrganizationsMap$,
        (services, transformationNames, associatedOrgs) => {
            const transformations: Transformation[] = CommonUtilsService.cloneObject(
                services?.[serviceId]?.transformations?.outbound || []
            );
            transformations
                .sort((a, b) => {
                    if (transformationNames?.includes(a.chainName) && transformationNames?.includes(b.chainName)) {
                        return transformationNames?.indexOf(a.chainName) - transformationNames?.indexOf(b.chainName);
                    } else if (transformationNames?.includes(a.chainName)) {
                        return -1;
                    } else if (transformationNames?.includes(b.chainName)) {
                        return 1;
                    }
                    return 0;
                })
                .forEach(transformation => {
                    transformation.active = transformationNames?.indexOf(transformation.chainName) > -1;
                    transformation.organizationName =
                        associatedOrgs?.[transformation.repositoryId]?.name || transformation.organizationName;
                });
            return transformations;
        }
    );

const getTransformationsRelativeOrganizations$ = (restApiName: string) =>
    createSelector(getServiceTransformations$(restApiName), map => map?.relativeOrganizations);

export const getOutboundTransformationsMap$ = (restApiName: string) =>
    createSelector(
        getOutBoundTransformations$(restApiName),
        getTransformationsRelativeOrganizations$(restApiName),
        getCurrentOrganization$,
        (transformations, orgNames, currentOrganization) => {
            const providerOrgId = currentOrganization?.providerOrganizationId;
            const organizationsList: {
                orgName: string;
                transformations: Transformation[];
                repositoryId: string;
            }[] = [];
            transformations.forEach(transformation => {
                if (!organizationsList.find(org => org.repositoryId === transformation.repositoryId)) {
                    const rawOrgName = orgNames?.[transformation.repositoryId] || transformation?.repositoryId;
                    organizationsList.push({
                        orgName: providerOrgId && transformation.repositoryId === providerOrgId ? 'Default' : rawOrgName,
                        repositoryId: transformation.repositoryId,
                        transformations: [],
                    });
                }
                const org = organizationsList.find(org => org.repositoryId === transformation.repositoryId);
                org.transformations.push(transformation);
            });
            return organizationsList;
        }
    );

export const getInBoundTransformations$ = (restApiName: string) =>
    createSelector(
        getAllTransformationsServicesMap$,
        getActiveInboundTransformations$(restApiName),
        getAssociatedOrganizationsMap$,
        (services, transformationNames = [], associatedOrgs) => {
            const transformations: Transformation[] = CommonUtilsService.cloneObject(
                services?.[restApiName]?.transformations?.inbound || []
            );
            transformations
                .sort((a, b) => {
                    if (transformationNames?.includes(a.chainName) && transformationNames?.includes(b.chainName)) {
                        return transformationNames?.indexOf(a.chainName) - transformationNames?.indexOf(b.chainName);
                    } else if (transformationNames?.includes(a.chainName)) {
                        return -1;
                    } else if (transformationNames?.includes(b.chainName)) {
                        return 1;
                    }
                    return 0;
                })
                .forEach(transformation => {
                    transformation.active = transformationNames?.indexOf(transformation.chainName) > -1;
                    transformation.organizationName =
                        associatedOrgs?.[transformation.repositoryId]?.name || transformation.organizationName;
                });
            return transformations;
        }
    );

const getTransformationsLoading$ = (restApiName: string) =>
    createSelector(getServiceTransformations$(restApiName), state => {
        return state?.loading;
    });

export const getInboundLoadingStatus$ = (restApiName: string) =>
    createSelector(getTransformationsLoading$(restApiName), loadingMap => {
        return loadingMap?.inbound;
    });

export const getOutboundLoadingStatus$ = (restApiName: string) =>
    createSelector(getTransformationsLoading$(restApiName), loadingMap => {
        return loadingMap?.outbound;
    });

export const getChainLoadingStatus$ = (restApiName: string) =>
    createSelector(getTransformationsLoading$(restApiName), loadingMap => {
        return loadingMap?.chainNames;
    });

export const getTransformationDetailLoadingStatus$ = (restApiName: string) =>
    createSelector(getTransformationsLoading$(restApiName), loadingMap => {
        return loadingMap?.transformationsDetail;
    });

export const getMasterSchedulerInBoundTransformations$ = createSelector(
    getSelectedAppRestApiName$,
    getAllTransformationsServicesMap$,
    (restApiName, transformationNamesMap) => {
        const transformations = CommonUtilsService.cloneObject(transformationNamesMap?.[restApiName]?.transformations);
        const chains: Chain[] = transformations?.masterScheduler?.inbound || [];
        const activeTransformations = transformations?.activeTransformations?.inbound || [];
        return chains
            .sort((a, b) => {
                if (activeTransformations?.includes(a.chainName) && activeTransformations?.includes(b.chainName)) {
                    return activeTransformations?.indexOf(a.chainName) - activeTransformations?.indexOf(b.chainName);
                } else if (activeTransformations?.includes(a.chainName)) {
                    return -1;
                } else if (activeTransformations?.includes(b.chainName)) {
                    return 1;
                }
                return 0;
            })
            .filter(chain => activeTransformations?.length === 0 || activeTransformations?.includes(chain.chainName));
    }
);

export const getInboundIntegrationsChains$ = createSelector(
    getSelectedAppRestApiName$,
    getAllTransformationsServicesMap$,
    (restApiName, transformationNamesMap) => {
        const transformations = CommonUtilsService.cloneObject(transformationNamesMap?.[restApiName]?.transformations);
        const chains: Chain[] = transformations?.integrationsChains?.inbound || [];
        const activeTransformations = transformations?.activeTransformations?.inbound || [];
        return chains
            .sort((a, b) => {
                if (activeTransformations?.includes(a.chainName) && activeTransformations?.includes(b.chainName)) {
                    return activeTransformations?.indexOf(a.chainName) - activeTransformations?.indexOf(b.chainName);
                } else if (activeTransformations?.includes(a.chainName)) {
                    return -1;
                } else if (activeTransformations?.includes(b.chainName)) {
                    return 1;
                }
                return 0;
            })
            .filter(chain => activeTransformations?.length === 0 || activeTransformations?.includes(chain.chainName));
    }
);

export const getOutboundIntegrationsChains$ = createSelector(
    getSelectedAppRestApiName$,
    getAllTransformationsServicesMap$,
    (restApiName, transformationNamesMap) => {
        const transformations = CommonUtilsService.cloneObject(transformationNamesMap?.[restApiName]?.transformations);
        const chains: Chain[] = transformations?.integrationsChains?.outbound || [];
        const activeTransformations = transformations?.activeTransformations?.outbound || [];
        return chains
            .sort((a, b) => {
                if (activeTransformations?.includes(a.chainName) && activeTransformations?.includes(b.chainName)) {
                    return activeTransformations?.indexOf(a.chainName) - activeTransformations?.indexOf(b.chainName);
                } else if (activeTransformations?.includes(a.chainName)) {
                    return -1;
                } else if (activeTransformations?.includes(b.chainName)) {
                    return 1;
                }
                return 0;
            })
            .filter(chain => activeTransformations?.length === 0 || activeTransformations?.includes(chain.chainName));
    }
);

export const getTransformationNamesInChain$ = (restApiName: string) =>
    createSelector(getAllTransformationsServicesMap$, services => services?.[restApiName]?.transformationsByChainName);

export const getOutboundChains$ = (restApiName: string) =>
    createSelector(getServiceTransformations$(restApiName), transformationsMap => transformationsMap?.transformations?.chains);

export const getChainsAndTransformations$ = createSelector(
    getSelectedAppRestApiName$,
    getAllTransformationsServicesMap$,
    (restApiName, transformationNamesMap) => {
        const transformations = CommonUtilsService.cloneObject(transformationNamesMap?.[restApiName]?.transformations);
        const chainsAndTransformation: Chain[] = transformations?.chainsAndTransformations?.outbound || [];
        return chainsAndTransformation;
    }
);
